import React, { useEffect, useState } from "react";
import { FaFileExcel, FaPlay, FaSpinner, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  defaultAxiosInstance,
  defaultAxiosInstanceReward,
  defaultAxiosInstanceRewardV2,
} from "../api/defaultInstance";
import "../css/AdManagement.css";
import "../css/AdminCampaignManagement.css";
import Layout from "./Layout";

function AdminCampaignManagement() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState({ totalCount: 0 });
  const [filter, setFilter] = useState({
    status: "전체",
    startDate: "",
    keyword: "",
    page: 1,
    limit: 200,
    offerMetaId: "전체",
    distributorNameEn: "전체",
  });
  const [selectedAds, setSelectedAds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  // 추가: offer, distributors 데이터 상태
  const [offerData, setOfferData] = useState(null);
  const [distributorsData, setDistributorsData] = useState(null);
  const [excelUploadError, setExcelUploadError] = useState(null);
  const fileInputRef = React.useRef();

  // 추가: 정답 관리 모달 상태
  const [showSightModal, setShowSightModal] = useState(false);
  const [sightModalData, setSightModalData] = useState({
    campaignMetaId: null,
    campaignMissions: [],
    placeName: "",
    placeId: "",
  });
  const [sightChanges, setSightChanges] = useState([]);

  // 추가: 키워드 수정 모달 상태
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [keywordModalData, setKeywordModalData] = useState({
    campaignMetaId: null,
    keywords: [],
    campaignMissions: [],
    placeName: "",
    placeId: "",
  });
  const [keywordChanges, setKeywordChanges] = useState([]);

  // 추가: 플레이스명 변경 모달 상태
  const [showPlaceNameModal, setShowPlaceNameModal] = useState(false);
  const [placeNameModalData, setPlaceNameModalData] = useState({
    campaignMetaId: null,
    campaignMissions: [],
    placeName: "",
    placeId: "",
    distributor: "",
  });
  const [newPlaceName, setNewPlaceName] = useState("");

  // --- 추가: offer, distributors 데이터 fetch 함수 ---
  const fetchOfferData = async () => {
    try {
      const response = await defaultAxiosInstanceReward.get(
        "/offer/list/by-accountId",
        {
          params: { accountId: 1 },
        },
      );
      setOfferData(response.data.data);
    } catch (error) {
      console.error("Error fetching offer data:", error);
    }
  };
  const fetchDistributorsData = async () => {
    try {
      const response = await defaultAxiosInstanceReward.get(
        "/reward-admin/campaign/distributors",
      );
      setDistributorsData(response.data.data);
    } catch (error) {
      console.error("Error fetching distributors data:", error);
    }
  };

  // --- 기존 캠페인 데이터 fetch ---
  const fetchTableData = async (currentFilter = filter) => {
    try {
      const response = await defaultAxiosInstanceReward.get(
        "/reward-admin/campaign/list",
        {
          params: currentFilter,
        },
      );
      const result = response.data.data;
      setCampaigns(result.campaigns || []);
      setSummary({ totalCount: result.totalCount || 0 });
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  // --- useEffect: 진입 시 3개 API 병렬 호출 ---
  useEffect(() => {
    fetchTableData();
    fetchOfferData();
    fetchDistributorsData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;

    console.log("e.target: ", e.target);

    if (type === "checkbox") {
      // boolean 필터 (submitted, paid)
      if (name === "submitted" || name === "paid") {
        setFilter((prev) => ({ ...prev, [name]: checked ? true : null }));
      }
      // 복수 선택 (advertisers)
      else if (name === "advertisers") {
        setFilter((prev) => {
          const updated = checked
            ? [...prev.advertisers, value]
            : prev.advertisers.filter((v) => v !== value);
          return { ...prev, advertisers: updated };
        });
      }
    } else {
      // 일반 input
      setFilter((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 필터 적용 시 페이지를 1로 리셋하고, 두 API 호출
  const applyFilter = () => {
    const newFilter = { ...filter, page: 1 };
    console.log("===newFilter", newFilter);
    setFilter(newFilter);
    setSelectedAds([]); // 선택된 캠페인 초기화
    fetchTableData(newFilter);
  };

  const resetFilter = () => {
    const initialFilter = {
      status: "전체",
      startDate: "",
      keyword: "",
      page: 1,
      limit: 200,
      offerMetaId: "전체",
      distributorNameEn: "전체",
    };
    setFilter(initialFilter);
    setSelectedAds([]);
    fetchTableData(initialFilter);
  };

  // 선택/체크박스 관리 ad.adCampaignMetaId → ad.campaignId
  const handleCheckboxChange = (campaignMetaId) => {
    setSelectedAds((prev) =>
      prev.includes(campaignMetaId)
        ? prev.filter((id) => id !== campaignMetaId)
        : [...prev, campaignMetaId],
    );
  };

  // 헤더 체크박스: 전체 선택/해제 기능
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAds(campaigns.map((ad) => ad.campaignMetaId));
    } else {
      setSelectedAds([]);
    }
  };

  // 페이징: 이전 버튼 클릭 시 테이블 데이터만 조회
  const handlePrevPage = () => {
    const newPage = Math.max(1, filter.page - 1);
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  // 페이징: 다음 버튼 클릭 시 테이블 데이터만 조회
  const handleNextPage = () => {
    const newPage = filter.page + 1;
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  const handlePageClick = (page) => {
    const newFilter = { ...filter, page };
    setFilter(newFilter);
    fetchTableData(newFilter);
  };

  // --- 엑셀 내보내기 함수 ---
  const exportToExcel = async () => {
    setIsLoading(true); // 로딩 시작

    let dataToExport = [];

    if (selectedAds.length > 0) {
      // 1. 테이블 행에서 선택한 경우 기존 테이블 데이터 중 선택된 행을 사용
      dataToExport = campaigns.filter((ad) =>
        selectedAds.includes(ad.campaignMetaId),
      );
    } else {
      // 2. 필터에 맞는 전체 데이터(페이징 되지 않음)
      dataToExport = campaigns;
    }

    // 3. 데이터 그룹핑 (광고주 및 productType 조건에 따른 매핑)
    const groupedData = {
      "호올스 저장": [],
      "호올스 유입": [],
      토스저장: [],
      토스유입: [],
      "버즈빌 유입": [],
      "버즈빌 저장": [],
      베이직: [],
      멘토스: [],
      브라보: [],
      찰리: [],
      길찾기저장: [],
      길찾기유입: [],
      제주: [],
      투플저장: [],
      투플유입: [],
      최적화유입: [],
      핫팩: [],
    };

    dataToExport.forEach((meta) => {
      meta.campaigns.forEach((campaign) => {
        // 광고주/상품타입별로 분류
        if (meta.advertiserName === "호올스") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["호올스 저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["호올스 유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "토스") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["토스저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["토스유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "버즈빌") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["버즈빌 저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["버즈빌 유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "파이") {
          groupedData["파이"].push({ meta, campaign });
        } else if (meta.advertiserName === "베이직") {
          groupedData["베이직"].push({ meta, campaign });
        } else if (meta.advertiserName === "멘토스") {
          groupedData["멘토스"].push({ meta, campaign });
        } else if (meta.advertiserName === "브라보") {
          groupedData["브라보"].push({ meta, campaign });
        } else if (meta.advertiserName === "찰리") {
          groupedData["찰리"].push({ meta, campaign });
        } else if (meta.advertiserName === "제주") {
          groupedData["제주"].push({ meta, campaign });
        } else if (meta.advertiserName === "길찾기") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["길찾기저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["길찾기유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "카카오") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["카카오저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["카카오유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "투플") {
          if (meta.productType === "저장" || meta.productType === "저장+유입") {
            groupedData["투플저장"].push({ meta, campaign });
          } else if (meta.productType === "유입") {
            groupedData["투플유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "최적화") {
          if (meta.productType === "유입") {
            groupedData["최적화유입"].push({ meta, campaign });
          }
        } else if (meta.advertiserName === "핫팩") {
          groupedData["핫팩"].push({ meta, campaign });
        }
      });
    });

    // 4. 시트별 데이터 변환 함수 (각 시트의 열 구성이 다름)
    const transformHoolse = ({ meta, campaign }) => {
      const start = new Date(meta.startDate);
      const end = new Date(meta.endDate);
      const deadline = new Date(end);
      deadline.setDate(deadline.getDate() + 1);
      const workingDays = Math.ceil((deadline - start) / (1000 * 60 * 60 * 24));

      return {
        신청자: meta.name,
        상품: meta.productType,
        접수일: meta.applyDate,
        MID: meta.placeId,
        "플레이스명 (업체명)": meta.placeName,
        "작업키워드 (검색시 키워드 5위 이내)":
          campaign.keywords[0]?.keyword || "",
        "일 요청량": campaign.dailyInflow,
        시작일: meta.startDate,
        마감일: meta.endDate,
        작업일: workingDays,
        총수량: campaign.totalInflow,
        특이사항: "",
        "정산액(포함)": campaign.totalCost,
        정산: "완료",
        세팅: true,
        검수자: "",
      };
    };

    const transformToss = ({ meta, campaign }) => {
      // 토스 시트 (저장/유입)
      const baseData = {
        신청자: meta.name,
        "플레이스명(업체명)": meta.placeName,
        "플레이스 URL": `https://m.place.naver.con/place/${meta.placeId}`,
        공백1: "",
        공백2: "",
        "요청 트래픽": campaign.dailyInflow,
        공백3: "",
        "검색어(단일 플레이스 키워드 구지도/신지도)":
          campaign.keywords[0]?.keyword || "",
      };

      if (campaign.productType === "유입") {
        return { ...baseData, 구분: "명소", 시작일자: meta.startDate };
      } else {
        return { ...baseData, 시작일자: meta.startDate };
      }
    };

    const transformBuzzvil = ({ meta, campaign }) => {
      return {
        신청자: meta.name,
        시작일: meta.startDate,
        마감일: meta.endDate,
        유입: campaign.dailyInflow,
        키워드: campaign.keywords[0]?.keyword || "",
        플레이스ID: meta.placeId,
        메모: "",
      };
    };

    // 추가: 구지도용 시트들
    const transformBasicGujiDo = ({ meta, campaign }) => {
      const start = new Date(meta.startDate);
      const end = new Date(meta.endDate);
      const workingDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return {
        신청자: meta.name,
        요청일: meta.applyDate,
        시작일: meta.startDate,
        종료일: meta.endDate,
        작업일: workingDays,
        검색어: campaign.keywords[0]?.keyword || "",
        플레이스ID: meta.placeId,
        일유입량: campaign.dailyInflow,
        플레이스링크: `https://m.place.naver.con/place/${meta.placeId}`,
        상호명: meta.placeName,
        총유입량: campaign.totalInflow,
        세팅유무: "세팅완료",
        정산: "체크",
        입금액: campaign.totalCost / 1.1,
        "입금액(VAT포함)": campaign.totalCost,
      };
    };

    const transformMentos = ({ meta, campaign }) => {
      return {
        신청자: meta.name,
        시작일: meta.startDate,
        마감일: meta.endDate,
        유입: campaign.dailyInflow,
        키워드: campaign.keywords[0]?.keyword || "",
        플레이스ID: meta.placeId,
        메모: "",
      };
    };

    const transformBravoOrCharlie = ({ meta, campaign }) => {
      const keywords = campaign.keywords.map((k) => k.keyword);
      return {
        신청자: meta.name,
        "(필수)업체명": meta.placeName,
        "(필수)업체URL": `https://m.place.naver.com/place/${meta.placeId}`,
        "(필수)업체PID번호": meta.placeId,
        "(필수)순위키워드": keywords[0] || "",
        "(필수)일목표\n10 이상": campaign.dailyInflow || 10,
        "(필수)일수\n1 이상": Math.max(
          1,
          parseInt(campaign.executionDays) || 1,
        ),
        "(필수)검색키워드1\n(5위 이내)": keywords[0] || "",
        "(선택)검색키워드2\n(5위 이내)": keywords[1] || "",
        "(선택)검색키워드3\n(5위 이내)": keywords[2] || "",
        "(선택)검색키워드4\n(5위 이내)": keywords[3] || "",
        "(선택)블로그 URL": "",
      };
    };

    const transformJamonglab = ({ meta, campaign }) => {
      const keywords = campaign.keywords.map((k) => k.keyword);
      const start = new Date(meta.startDate);
      const end = new Date(meta.endDate);
      const deadline = new Date(end);
      deadline.setDate(deadline.getDate() + 1);
      const workingDays = Math.ceil((deadline - start) / (1000 * 60 * 60 * 24));

      return {
        신청자: meta.name,
        접수일: meta.applyDate,
        시작일: meta.startDate,
        종료일: meta.endDate,
        작업일: workingDays,
        최대참여: campaign.totalInflow,
        하루참여: campaign.dailyInflow,
        공백: "",
        키워드: keywords.join(", "),
        "플레이스 URL": `https://m.place.naver.com/place/${meta.placeId}`,
      };
    };

    const transformTople = ({ meta, campaign }) => {
      const keywords = campaign.keywords.map((k) => k.keyword);
      const start = new Date(meta.startDate);
      const end = new Date(meta.endDate);
      const deadline = new Date(end);
      deadline.setDate(deadline.getDate() + 1);
      const workingDays = Math.ceil((deadline - start) / (1000 * 60 * 60 * 24));

      return {
        신청자: meta.name,
        접수일: meta.applyDate,
        시작일: meta.startDate,
        종료일: meta.endDate,
        작업일: workingDays,
        최대참여: campaign.totalInflow,
        하루참여: campaign.dailyInflow,
        공백: "",
        키워드: keywords.join(", "),
        "플레이스 URL": `https://m.place.naver.com/place/${meta.placeId}`,
      };
    };

    const transformJeju = ({ meta, campaign }) => {
      const start = new Date(meta.startDate);
      const end = new Date(meta.endDate);
      const deadline = new Date(end);
      deadline.setDate(deadline.getDate() + 1);
      const workingDays = Math.ceil((deadline - start) / (1000 * 60 * 60 * 24));

      return {
        신청자: meta.name,
        "플레이스 URL": `https://m.place.naver.com/place/${meta.placeId}`,
        "순위 조회 키워드": campaign.keywords[0]?.keyword || "",
        "작업 키워드": campaign.keywords[0]?.keyword || "",
        작업일수: workingDays,
        "일작업량 (최소50)": campaign.dailyInflow,
        "작업 시작일": meta.startDate,
        메모: "",
      };
    };

    // 5. 워크북 생성 후 시트별로 데이터 추가
    const wb = XLSX.utils.book_new();

    // helper 함수: 데이터가 있을 때만 시트 생성
    const addSheet = (sheetName, data, transformFn) => {
      if (data?.length > 0) {
        const transformedData = data.map((row) => transformFn(row));
        const ws = XLSX.utils.json_to_sheet(transformedData);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    };

    addSheet("호올스 저장", groupedData["호올스 저장"], transformHoolse);
    addSheet("호올스 유입", groupedData["호올스 유입"], transformHoolse);
    addSheet("토스유입", groupedData["토스유입"], (row) => transformToss(row));
    addSheet("토스저장", groupedData["토스저장"], (row) => transformToss(row));
    addSheet("버즈빌 유입", groupedData["버즈빌 유입"], transformBuzzvil);
    addSheet("버즈빌 저장", groupedData["버즈빌 저장"], transformBuzzvil);
    // 추가된 길찾기 시트들
    addSheet("베이직", groupedData["베이직"], transformBasicGujiDo);
    addSheet("멘토스(12시반)", groupedData["멘토스"], transformMentos);
    addSheet("브라보", groupedData["브라보"], transformBravoOrCharlie);
    addSheet("찰리", groupedData["찰리"], transformBravoOrCharlie);
    addSheet("길찾기저장", groupedData["길찾기저장"], (row) =>
      transformJamonglab(row),
    );
    addSheet("길찾기유입", groupedData["길찾기유입"], (row) =>
      transformJamonglab(row),
    );
    addSheet("제주", groupedData["제주"], transformJeju);
    addSheet("투플저장", groupedData["투플저장"], (row) => transformTople(row));
    addSheet("투플유입", groupedData["투플유입"], (row) => transformTople(row));
    addSheet("최적화유입", groupedData["최적화유입"], (row) =>
      transformTople(row),
    );
    addSheet("핫팩", groupedData["핫팩"], (row) => transformTople(row));
    // 6. 엑셀 파일 생성 및 다운로드
    XLSX.writeFile(wb, "exported_campaigns.xlsx");

    setIsLoading(false); // 로딩 종료
  };

  // 키워드 클릭 시 네이버 검색 링크 열기 (키워드 수정 모달에서만)
  const handleKeywordClick = (keyword) => {
    console.log("keyword: ", keyword);
    if (showKeywordModal) {
      const searchUrl = `https://m.search.naver.com/search.naver?query=${encodeURIComponent(keyword)}&sm=mtp_hty.top&where=m`;
      window.open(searchUrl, "_blank");
    }
  };

  const handleKeywordBlur = async (ad, newKeyword) => {
    if (!editingKeyword || editingKeyword.currentKeyword === newKeyword) {
      setEditingKeyword(null);
      return;
    }

    try {
      const response = await defaultAxiosInstance.post(
        "/advertisement/admin/keyword",
        {
          keywordId: editingKeyword.keywordId,
          currentKeyword: editingKeyword.currentKeyword,
          newKeyword: newKeyword,
          placeName: ad.placeName,
          placeId: ad.placeId,
        },
      );

      if (response.data.status === 200) {
        // 성공적으로 업데이트된 경우 테이블 데이터 새로고침
        fetchTableData();
      }
    } catch (error) {
      console.error("키워드 수정 실패:", error);
      alert("키워드 수정에 실패했습니다.");
    }

    setEditingKeyword(null);
  };

  // 날짜 수정 핸들러 수정
  const handleDateClick = (campaignId, startDate, endDate) => {
    setEditingDate({ campaignId, startDate, endDate });
  };

  const handleDateChange = async (ad, newStartDate) => {
    if (!editingDate) return;

    // 날짜 차이 계산 (원래 종료일 - 시작일)
    const originalStartDate = new Date(editingDate.startDate);
    const originalEndDate = new Date(editingDate.endDate);
    const daysDiff = Math.ceil(
      (originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24),
    );

    // 새로운 종료일 계산
    const newStartDateTime = new Date(newStartDate);
    const newEndDate = new Date(newStartDateTime);
    newEndDate.setDate(newEndDate.getDate() + daysDiff);

    try {
      const response = await defaultAxiosInstance.put(
        "/advertisement/admin/campaign/date",
        {
          campaignId: editingDate.campaignId,
          startDate: newStartDate,
          endDate: newEndDate.toISOString().split("T")[0],
        },
      );

      if (response.data.status === 200) {
        // 성공적으로 업데이트된 경우 테이블 데이터 새로고침
        fetchTableData();
      }
    } catch (error) {
      console.error("날짜 수정 실패:", error);
      alert("날짜 수정에 실패했습니다.");
    }

    setEditingDate(null);
  };

  // 엑셀 업로드 및 파싱 핸들러
  const handleExcelUpload = async (e) => {
    setExcelUploadError(null);
    const file = e.target.files[0];
    if (!file) return;

    if (!file) return;

    try {
      // FormData 생성
      const formData = new FormData();
      formData.append("file", file);
      formData.append("accountId", 1);

      // 엑셀 파일을 서버로 전송
      await defaultAxiosInstanceRewardV2.post(
        "/reward-admin/campaign/upload/excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // 캠페인 목록 새로고침
      // fetchTableData();

      alert("엑셀 대량등록 요청 완료. 완료되는데 시간이 소요됩니다..");
    } catch (err) {
      setExcelUploadError(err.message);
      console.log(err);
      alert(err.response.data.data.message);
    } finally {
      e.target.value = "";
    }

    /*
        const manager = new ExcelBulkRegisterManager();
        
        try {
            // 파싱 결과를 상태로 저장
            const excelParsingResult = await manager.parseFile(file, offerData, distributorsData);

            // 파싱 결과를 서버로 전송
            await manager.sendToServer(excelParsingResult);

            // 캠페인 목록 새로고침
            //fetchTableData();
            
            alert('엑셀 대량등록 요청 완료');
        } catch (err) {
            setExcelUploadError(err.message);
            alert(err.message);
        } finally {
            e.target.value = '';
        }
    */
  };

  // 엑셀 대량등록 버튼 클릭 시 input[type=file] 트리거
  const handleExcelButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // 정답 관리 모달 열기
  const handleSightManageClick = (campaign) => {
    console.log("campaign: ", campaign);
    setSightModalData({
      campaignMetaId: campaign.campaignMetaId,
      campaignMissions: campaign.campaignMissions || [],
      placeName: campaign.placeName,
      placeId: campaign.placeId,
    });
    setShowSightModal(true);
  };

  // 정답 변경 핸들러
  const handleSightChange = (campaignId, field, value) => {
    setSightChanges((prev) => {
      const existingIndex = prev.findIndex(
        (change) => change.campaignId === campaignId,
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], [field]: value };
        return updated;
      } else {
        return [...prev, { campaignId, [field]: value }];
      }
    });
  };

  // 정답 관리 모달 저장
  const handleSightUpdate = async () => {
    if (sightChanges.length === 0) {
      alert("변경된 정답이 없습니다.");
      return;
    }

    try {
      setIsLoading(true);

      // mission별로 변경사항 분류
      const smartCallChanges = [];
      const findingRoadChanges = [];
      const findingSightChanges = [];
      const findingParkingLotChanges = [];
      const findingReviewCountChanges = [];

      for (const change of sightChanges) {
        const mission = sightModalData.campaignMissions.find(
          (m) => m.campaignId === change.campaignId,
        );
        if (!mission) continue;

        if (mission.smartCall && change.answer !== undefined) {
          smartCallChanges.push({
            campaignId: change.campaignId,
            answer: change.answer,
          });
        } else if (
          mission.findingRoad &&
          (change.sightRank !== undefined ||
            change.sightName !== undefined ||
            change.answer !== undefined)
        ) {
          findingRoadChanges.push({
            campaignId: change.campaignId,
            sightRank:
              change.sightRank !== undefined
                ? parseInt(change.sightRank)
                : mission.findingRoad.sightRank,
            sightName:
              change.sightName !== undefined
                ? change.sightName
                : mission.findingRoad.sightName,
            answer:
              change.answer !== undefined
                ? change.answer
                : mission.findingRoad.answer,
            distributor: mission.findingRoad.distributorNameEn,
          });
        } else if (mission.findingSight && change.answer !== undefined) {
          findingSightChanges.push({
            campaignId: change.campaignId,
            answer: change.answer,
            distributor: mission.findingSight.distributorNameEn,
          });
        } else if (mission.findingParkingLot && change.answer !== undefined) {
          findingParkingLotChanges.push({
            campaignId: change.campaignId,
            answer: change.answer,
            distributor: mission.findingParkingLot.distributorNameEn,
          });
        } else if (mission.findingReviewCount && change.answer !== undefined) {
          findingReviewCountChanges.push({
            campaignId: change.campaignId,
            answer: change.answer,
            distributor: mission.findingReviewCount.distributorNameEn,
          });
        }
      }

      // SmartCall API 호출
      if (smartCallChanges.length > 0) {
        await defaultAxiosInstanceReward.put(
          "/reward-admin/campaign/smartcall/answer/update/bulk",
          {
            campaignInfoList: smartCallChanges,
          },
        );
      }

      // FindingRoad API 호출
      if (findingRoadChanges.length > 0) {
        await defaultAxiosInstanceReward.put(
          "/reward-admin/campaign/find-road/answer/update/bulk",
          {
            campaignInfoList: findingRoadChanges,
          },
        );
      }

      // FindingSight API 호출
      if (findingSightChanges.length > 0) {
        await defaultAxiosInstanceReward.put(
          "/reward-admin/campaign/find-sight/answer/update/bulk",
          {
            campaignInfoList: findingSightChanges,
          },
        );
      }

      // FindingParkingLot API 호출
      if (findingParkingLotChanges.length > 0) {
        await defaultAxiosInstanceReward.put(
          "/reward-admin/campaign/find-parking-lot/answer/update/bulk",
          {
            campaignInfoList: findingParkingLotChanges,
          },
        );
      }

      // FindingReviewCount API 호출
      if (findingReviewCountChanges.length > 0) {
        await defaultAxiosInstanceReward.put(
          "/reward-admin/campaign/find-review-count/answer/update/bulk",
          {
            campaignInfoList: findingReviewCountChanges,
          },
        );
      }

      alert("정답이 성공적으로 수정되었습니다.");
      setShowSightModal(false);
      setSightChanges([]);
      fetchTableData(); // 테이블 데이터 새로고침
    } catch (error) {
      console.error("정답 수정 실패:", error);
      alert("정답 수정에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 정답 관리 모달 취소
  const handleSightCancel = () => {
    setShowSightModal(false);
    setSightChanges([]);
  };

  // 행 클릭 시 키워드 수정 모달 열기
  const handleRowClick = (campaign) => {
    setKeywordModalData({
      campaignMetaId: campaign.campaignMetaId,
      keywords: campaign.keywords || [],
      campaignMissions: campaign.campaignMissions || [],
      placeName: campaign.placeName,
      placeId: campaign.placeId,
    });
    setKeywordChanges([]);
    setShowKeywordModal(true);
  };

  // 키워드 변경 핸들러
  const handleKeywordChange = (keywordId, newKeyword) => {
    setKeywordChanges((prev) => {
      const existingIndex = prev.findIndex(
        (change) => change.campaignKeywordId === keywordId,
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], newKeyword };
        return updated;
      } else {
        const originalKeyword = keywordModalData.keywords.find(
          (k) => k.campaignKeywordId === keywordId,
        );
        return [
          ...prev,
          {
            campaignKeywordId: keywordId,
            previousKeyword: originalKeyword.keyword,
            newKeyword: newKeyword,
            rank: originalKeyword.rank,
            type: originalKeyword.type,
          },
        ];
      }
    });
  };

  // 키워드 수정 모달 저장
  const handleKeywordSave = async () => {
    if (keywordChanges.length === 0) {
      alert("변경된 키워드가 없습니다.");
      return;
    }

    try {
      setIsLoading(true);

      const requestData = [
        {
          campaignMetaId: keywordModalData.campaignMetaId,
          keywords: keywordChanges,
          campaignIdsInfo: keywordModalData.campaignMissions.map((mission) => ({
            campaignId: mission.campaignId,
            distributorNameEn:
              mission.smartCall?.distributorNameEn ||
              mission.findingRoad?.distributorNameEn ||
              mission.findingSight?.distributorNameEn ||
              mission.findingParkingLot?.distributorNameEn ||
              mission.findingReviewCount?.distributorNameEn ||
              mission.save?.distributorNameEn,
          })),
        },
      ];

      const response = await defaultAxiosInstanceReward.put(
        "/reward-admin/campaign/meta/keyword/update/bulk",
        requestData,
      );

      if (response.data.status === 200) {
        alert("키워드가 성공적으로 수정되었습니다.");
        setShowKeywordModal(false);
        fetchTableData(); // 테이블 데이터 새로고침
      }
    } catch (error) {
      console.error("키워드 수정 실패:", error);
      alert("키워드 수정에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 키워드 수정 모달 취소
  const handleKeywordCancel = () => {
    setShowKeywordModal(false);
    setKeywordChanges([]);
  };

  // 플레이스명 클릭 시 모달 열기
  const handlePlaceNameClick = (campaign, e) => {
    e.stopPropagation(); // 행 클릭 이벤트 방지

    // campaignMissions에서 첫 번째 distributor 정보 가져오기
    const firstMission = campaign.campaignMissions?.[0];
    const distributor =
      firstMission?.smartCall?.distributorNameEn ||
      firstMission?.findingRoad?.distributorNameEn ||
      firstMission?.findingSight?.distributorNameEn ||
      firstMission?.findingParkingLot?.distributorNameEn ||
      firstMission?.findingReviewCount?.distributorNameEn ||
      firstMission?.save?.distributorNameEn ||
      campaign.distributorNameEn ||
      "";

    setPlaceNameModalData({
      campaignMetaId: campaign.campaignMetaId,
      campaignMissions: campaign.campaignMissions || [],
      placeName: campaign.placeName,
      placeId: campaign.placeId,
      distributor: distributor,
    });
    setNewPlaceName(campaign.placeName);
    setShowPlaceNameModal(true);
  };

  // 플레이스명 변경 저장
  const handlePlaceNameSave = async () => {
    if (!newPlaceName || newPlaceName.trim() === "") {
      alert("플레이스명을 입력해주세요.");
      return;
    }

    if (newPlaceName === placeNameModalData.placeName) {
      alert("변경된 플레이스명이 없습니다.");
      return;
    }

    if (!placeNameModalData.distributor) {
      alert("distributor 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      setIsLoading(true);

      // 각 campaignMission에 대해 API 호출
      console.log("placeNameModalData.campaignMissions", placeNameModalData);
      const updatePromises = placeNameModalData.campaignMissions.map(
        (mission) => {
          const campaignMetaId = mission.campaignMetaId;
          const distributor =
            mission.smartCall?.distributorNameEn ||
            mission.findingRoad?.distributorNameEn ||
            mission.findingSight?.distributorNameEn ||
            mission.findingParkingLot?.distributorNameEn ||
            mission.findingReviewCount?.distributorNameEn ||
            mission.save?.distributorNameEn ||
            placeNameModalData.distributor;

          return defaultAxiosInstanceReward.put(
            "/reward-admin/campaign/place-name/update",
            {
              campaignId: placeNameModalData.campaignMetaId,
              distributor: distributor,
              originalPlaceName: placeNameModalData.placeName,
              placeName: newPlaceName,
            },
          );
        },
      );

      await Promise.all(updatePromises);

      alert("플레이스명이 성공적으로 변경되었습니다.");
      setShowPlaceNameModal(false);
      fetchTableData(); // 테이블 데이터 새로고침
    } catch (error) {
      console.error("플레이스명 변경 실패:", error);
      alert("플레이스명 변경에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 플레이스명 변경 모달 취소
  const handlePlaceNameCancel = () => {
    setShowPlaceNameModal(false);
    setNewPlaceName("");
  };

  // 캠페인 중단 함수
  const handleStopCampaigns = async () => {
    if (selectedAds.length === 0) {
      alert("중단할 캠페인을 선택해주세요.");
      return;
    }

    // 선택된 캠페인 중 활성화 또는 종료임박 상태가 아닌 캠페인 확인
    const selectedCampaigns = campaigns.filter((ad) =>
      selectedAds.includes(ad.campaignMetaId),
    );
    const invalidCampaigns = selectedCampaigns.filter(
      (ad) => ad.status !== "ACTIVE" && ad.status !== "EXPIRING_SOON",
    );

    if (invalidCampaigns.length > 0) {
      const invalidNames = invalidCampaigns
        .map((ad) => ad.placeName)
        .join(", ");
      alert(
        `캠페인 중단은 활성화 또는 종료임박 상태의 캠페인만 가능합니다.\n\n적용 불가능한 캠페인: ${invalidNames}`,
      );
      return;
    }

    const confirmed = window.confirm(
      `선택된 ${selectedAds.length}개의 캠페인을 중단하시겠습니까?`,
    );
    if (!confirmed) return;

    try {
      setIsLoading(true);
      const response = await defaultAxiosInstanceReward.put(
        "/campaign/admin/stop",
        {
          campaignIds: selectedAds,
        },
      );

      if (response.data.status === 200) {
        alert("캠페인들이 성공적으로 중지되었습니다.");
        setSelectedAds([]); // 선택 초기화
        fetchTableData(); // 테이블 새로고침
      }
    } catch (error) {
      console.error("캠페인 중단 실패:", error);
      alert("캠페인 중단에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 캠페인 강제송출 함수
  const handleForcePushCampaigns = async () => {
    if (selectedAds.length === 0) {
      alert("강제송출할 캠페인을 선택해주세요.");
      return;
    }

    // 선택된 캠페인 중 활성화 또는 종료임박 상태가 아닌 캠페인 확인
    const selectedCampaigns = campaigns.filter((ad) =>
      selectedAds.includes(ad.campaignMetaId),
    );
    const invalidCampaigns = selectedCampaigns.filter(
      (ad) => ad.status !== "ACTIVE" && ad.status !== "EXPIRING_SOON",
    );

    if (invalidCampaigns.length > 0) {
      const invalidNames = invalidCampaigns
        .map((ad) => ad.placeName)
        .join(", ");
      alert(
        `캠페인 강제송출은 활성화 또는 종료임박 상태의 캠페인만 가능합니다.\n\n적용 불가능한 캠페인: ${invalidNames}`,
      );
      return;
    }

    const confirmed = window.confirm(
      `선택된 ${selectedAds.length}개의 캠페인을 강제송출하시겠습니까?`,
    );
    if (!confirmed) return;

    try {
      setIsLoading(true);

      // 선택된 캠페인들의 distributor 정보를 가져와서 API 요청 데이터 구성
      const forcePushData = selectedCampaigns.map((campaign) => ({
        campaignId: campaign.campaignId.toString(),
        distributor: campaign.distributorNameEn || campaign.distributorName,
      }));

      const response = await defaultAxiosInstanceReward.put(
        "/campaign/admin/forcedpush",
        forcePushData,
      );

      if (response.data.status === 200) {
        alert("캠페인 강제 송출이 성공적으로 완료되었습니다.");
        setSelectedAds([]); // 선택 초기화
        fetchTableData(); // 테이블 새로고침
      }
    } catch (error) {
      console.error("캠페인 강제송출 실패:", error);
      alert("캠페인 강제송출에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 캠페인 노출 함수
  const handleActivateCampaigns = async () => {
    if (selectedAds.length === 0) {
      alert("노출할 캠페인을 선택해주세요.");
      return;
    }

    // 선택된 캠페인 중 WAITING 상태가 아닌 캠페인 확인
    const selectedCampaigns = campaigns.filter((ad) =>
      selectedAds.includes(ad.campaignMetaId),
    );
    const invalidCampaigns = selectedCampaigns.filter(
      (ad) => ad.status !== "WRONG_ANSWER",
    );

    if (invalidCampaigns.length > 0) {
      const invalidNames = invalidCampaigns
        .map((ad) => ad.placeName)
        .join(", ");
      alert(
        `캠페인 노출은 오답 상태의 캠페인만 가능합니다.\n\n적용 불가능한 캠페인: ${invalidNames}`,
      );
      return;
    }

    const confirmed = window.confirm(
      `선택된 ${selectedAds.length}개의 캠페인을 노출하시겠습니까?`,
    );
    if (!confirmed) return;

    try {
      setIsLoading(true);

      // 선택된 캠페인들을 타입별로 그룹핑
      const campaignsByType = {};

      selectedCampaigns.forEach((campaign) => {
        campaign.campaignMissions.forEach((mission) => {
          let campaignType = null;

          // 미션 타입에 따라 캠페인 타입 결정
          if (mission.smartCall) {
            campaignType = "smartCall";
          } else if (mission.findingRoad) {
            campaignType = `findRoad:${mission.findingRoad.type}`;
          } else if (mission.findingSight) {
            campaignType = "findSight";
          } else if (mission.findingParkingLot) {
            campaignType = "findParkingLot";
          } else if (mission.findingReviewCount) {
            campaignType = "findReviewCount";
          } else if (mission.save) {
            campaignType = `save:${mission.save.type}`;
          }

          if (campaignType) {
            if (!campaignsByType[campaignType]) {
              campaignsByType[campaignType] = [];
            }
            campaignsByType[campaignType].push(mission.campaignId);
          }
        });
      });

      // API 요청 데이터 구성
      const activationData = Object.entries(campaignsByType).map(
        ([type, campaignIds]) => ({
          type: type,
          campaignIds: campaignIds,
        }),
      );

      console.log("캠페인 노출 요청 데이터:", activationData);

      const response = await defaultAxiosInstanceReward.put(
        "/reward-admin/campaign/activate",
        {
          campaigns: activationData,
        },
      );

      if (response.data.status === 200) {
        alert("캠페인들이 성공적으로 노출되었습니다.");
        setSelectedAds([]); // 선택 초기화
        fetchTableData(); // 테이블 새로고침
      }
    } catch (error) {
      console.error("캠페인 노출 실패:", error);
      alert("캠페인 노출에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 캠페인 중단 함수
  const handleDeleteCampaigns = async () => {
    if (selectedAds.length === 0) {
      alert("중단할 캠페인을 선택해주세요.");
      return;
    }

    // 선택된 캠페인 중 WAITING 상태가 아닌 캠페인 확인
    /*const selectedCampaigns = campaigns.filter(ad => selectedAds.includes(ad.campaignMetaId));
         const invalidCampaigns = selectedCampaigns.filter(ad => ad.status !== 'WAITING');
         
         if (invalidCampaigns.length > 0) {
             const invalidNames = invalidCampaigns.map(ad => ad.campaignMetaId).join(', ');
             alert(`캠페인 중단는 대기중 상태의 캠페인만 가능합니다.\n\n중단 불가능한 캠페인: ${invalidNames}`);
             return;
         }
        */

    const confirmed = window.confirm(
      `선택된 ${selectedAds.length}개의 캠페인을 중단하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`,
    );
    if (!confirmed) return;

    try {
      setIsLoading(true);

      const response = await defaultAxiosInstanceReward.post(
        "/reward-admin/campaign/meta/delete/bulk",
        {
          campaignMetaIds: selectedAds,
          isVisibleInHistory: true,
        },
      );

      if (response.data.status === 200) {
        alert("캠페인이 성공적으로 중단되었습니다.");
        setSelectedAds([]); // 선택 초기화
        fetchTableData(); // 테이블 새로고침
      }
    } catch (error) {
      console.error("캠페인 중단 실패:", error);
      alert("캠페인 중단에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        if (showSightModal) {
          setShowSightModal(false);
        }
        if (showKeywordModal) {
          setShowKeywordModal(false);
          setKeywordChanges([]);
        }
        if (showPlaceNameModal) {
          setShowPlaceNameModal(false);
          setNewPlaceName("");
        }
      }
    };

    if (showSightModal || showKeywordModal || showPlaceNameModal) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [showSightModal, showKeywordModal, showPlaceNameModal]);

  // 시작 인덱스와 끝 인덱스 계산
  const startIndex = (filter.page - 1) * filter.limit + 1;
  const endIndex = Math.min(filter.page * filter.limit, summary.totalCount);

  // 페이지네이션 페이지 번호 계산 (현재 페이지 주변만 표시)
  const totalPages = Math.ceil(summary.totalCount / filter.limit);
  const getPageNumbers = () => {
    const pages = [];
    const currentPage = filter.page;
    const maxVisible = 7; // 표시할 최대 페이지 수

    if (totalPages <= maxVisible) {
      // 전체 페이지가 적으면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 첫 페이지
      pages.push(1);

      if (currentPage <= 4) {
        // 앞부분: 1, 2, 3, 4, 5, ..., 마지막
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // 뒷부분: 1, ..., 마지막-4, 마지막-3, 마지막-2, 마지막-1, 마지막
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 중간: 1, ..., 현재-1, 현재, 현재+1, ..., 마지막
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <Layout>
      <div className="ad-management-container admin-campaign-page">
        <div className="filter-bar">
          <div className="filter-group">
            <label>시작일</label>
            <input
              type="date"
              name="startDate"
              onChange={handleFilterChange}
              value={filter.startDate}
            />
          </div>
          <div className="filter-group">
            <label>종료일</label>
            <input
              type="date"
              name="endDate"
              onChange={handleFilterChange}
              value={filter.endDate}
            />
          </div>
          <div className="filter-group">
            <label>개발사</label>
            <select
              name="distributorNameEn"
              onChange={handleFilterChange}
              value={filter.distributorNameEn}
            >
              <option value="전체">전체</option>
              <option value="konnet">코넷</option>
              <option value="buzzvil">버즈빌</option>
              <option value="avatye">아바티</option>
            </select>
          </div>
          <div className="filter-group">
            <label>미션 타입</label>
            <select
              name="offerMetaId"
              onChange={handleFilterChange}
              value={filter.offerMetaId}
            >
              <option value="전체">전체</option>
              {offerData ? (
                offerData.map((offer) => (
                  <option key={offer.offerMetaId} value={offer.offerMetaId}>
                    {offer.offerName}
                  </option>
                ))
              ) : (
                <option disabled>로딩 중...</option>
              )}
            </select>
          </div>
          <div className="filter-group">
            <label>상태</label>
            <select
              name="status"
              onChange={handleFilterChange}
              value={filter.status}
            >
              <option value="전체">전체</option>
              <option value="WAITING">대기중</option>
              <option value="ACTIVE">활성화</option>
              <option value="EXPIRING_SOON">종료임박</option>
              <option value="STOP_REQUEST">중단</option>
              <option value="WRONG_ANSWER">오답</option>
              <option value="TERMINATED">종료</option>
            </select>
          </div>
          <div className="filter-group searchbox">
            <label>검색</label>
            <input
              type="text"
              name="keyword"
              placeholder="플레이스명, MID값, 신청자, 캠페인아이디"
              onChange={handleFilterChange}
              value={filter.keyword}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
            />
          </div>
          <div className="filter-group">
            <button onClick={applyFilter}>필터 적용</button>
            <button onClick={resetFilter} className="reset-btn">
              초기화
            </button>
          </div>
        </div>

        <div className="table-header">
          <select
            value={filter.limit}
            onChange={(e) => {
              setFilter((prev) => ({
                ...prev,
                limit: Number(e.target.value),
                page: 1,
              }));
              fetchTableData({
                ...filter,
                limit: Number(e.target.value),
                page: 1,
              });
            }}
            style={{
              fontSize: "1.1em",
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              marginBottom: "10px",
            }}
          >
            <option value={50}>50개씩 보기</option>
            <option value={100}>100개씩 보기</option>
            <option value={200}>200개씩 보기</option>
            <option value={500}>500개씩 보기</option>
          </select>

          <div
            className="button-group"
            style={{
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {/* <button className="status-btn submitted" onClick={null}>
                            <FaCheckCircle /> 캠페인 생성
                        </button> */}

            {/*
                        <button className="status-btn terminated" onClick={handleStopCampaigns} disabled={isLoading || selectedAds.length === 0}>
                            <FaStop /> 캠페인 중단
                        </button>

                        <button className="status-btn submitted" onClick={handleForcePushCampaigns} disabled={isLoading || selectedAds.length === 0}>
                             <FaPlay /> 강제송출
                         </button>
                        */}

            {/* <button className="export-btn" onClick={exportToExcel} disabled={isLoading}>
                            {isLoading ? <FaSpinner className="spinner" /> : <FaFileExcel />}
                            {isLoading ? '요청 중...' : '시트로 내보내기'}
                        </button> */}

            <button
              className="export-btn"
              onClick={handleExcelButtonClick}
              disabled={isLoading}
            >
              {isLoading ? <FaSpinner className="spinner" /> : <FaFileExcel />}
              {isLoading ? "캠페인 정보 저장 중..." : "엑셀 대량등록"}
            </button>

            <input
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleExcelUpload}
            />
            {excelUploadError && (
              <div style={{ color: "red" }}>{excelUploadError}</div>
            )}

            <button
              className="status-btn submitted"
              onClick={handleActivateCampaigns}
              disabled={isLoading || selectedAds.length === 0}
            >
              <FaPlay /> 캠페인 노출
            </button>

            <button
              className="status-btn terminated"
              onClick={handleDeleteCampaigns}
              disabled={isLoading || selectedAds.length === 0}
            >
              <FaTrash /> 캠페인 중단
            </button>
            {/* <button className="status-btn paid" onClick={null}>
                            <FaCreditCard /> 키워드 검수&수정
                        </button> */}
          </div>
        </div>

        <div className="ad-table">
          <table>
            <thead>
              <tr>
                <th className="center-cell">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      campaigns.length > 0 &&
                      selectedAds.length === campaigns.length
                    }
                  />
                </th>
                <th className="center-cell">캠페인아이디</th>
                <th className="center-cell">
                  접수자(ID)
                  <br />
                  접수일
                  <br />
                  상태
                </th>
                <th className="center-cell">
                  시작일
                  <br />
                  종료일
                  <br />
                  작업일수
                </th>
                <th className="center-cell">
                  총수량
                  <br />
                  일수량
                  <br />
                  총금액(단가)
                </th>
                <th className="center-cell">
                  개발사/미션
                  <br />
                  플레이스명
                  <br />
                  MID
                </th>
                <th className="center-cell">키워드 1~5</th>
                <th className="center-cell">순위/타입</th>
                {(() => {
                  const selectedOffer =
                    offerData &&
                    offerData.find(
                      (offer) => offer.offerMetaId == filter.offerMetaId,
                    );
                  const shouldShow =
                    filter.offerMetaId !== "전체" &&
                    selectedOffer?.offerName === "도보 길찾기";
                  console.log("명소 컬럼 조건:", {
                    filterOfferMetaId: filter.offerMetaId,
                    selectedOffer,
                    shouldShow,
                  });
                  console.log("offerData: ", offerData);
                  return shouldShow && <th className="center-cell">명소</th>;
                })()}
                <th className="center-cell">정답관리</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((ad, idx) => (
                <tr
                  key={ad.campaignId}
                  className="center-cell"
                  onClick={() => handleRowClick(ad)}
                  style={{ cursor: "pointer" }}
                >
                  <td
                    className="center-cell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAds.includes(ad.campaignMetaId)}
                      onChange={() => handleCheckboxChange(ad.campaignMetaId)}
                    />
                  </td>
                  <td className="center-cell">{ad.campaignMetaId}</td>
                  <td className="center-cell">
                    <div>
                      <span className="user-name">
                        {ad.reqAccountName}
                        {ad.userId ? ` (${ad.userId})` : ""}
                      </span>
                    </div>
                    <div>{ad.reqDate ? ad.reqDate : <>&nbsp;</>}</div>
                    <div>
                      <span className={`badge badge-${ad.status}`}>
                        {ad.status == "ACTIVE"
                          ? "활성화"
                          : ad.status == "EXPIRING_SOON"
                            ? "종료임박"
                            : ad.status == "WAITING"
                              ? "대기중"
                              : ad.status == "TERMINATED"
                                ? "종료"
                                : ad.status == "STOP_REQUEST"
                                  ? "중단"
                                  : ad.status == "WRONG_ANSWER"
                                    ? "오답"
                                    : "UNKNOWN"}
                      </span>
                    </div>
                  </td>
                  <td className="center-cell">
                    <div>
                      {editingDate &&
                      editingDate.campaignId === ad.campaignId ? (
                        <input
                          type="date"
                          defaultValue={ad.startDate}
                          onChange={(e) => handleDateChange(ad, e.target.value)}
                        />
                      ) : (
                        <span
                          onClick={() =>
                            handleDateClick(
                              ad.campaignId,
                              ad.startDate,
                              ad.endDate,
                            )
                          }
                          style={{ cursor: "pointer" }}
                          className="date-blue"
                        >
                          {ad.startDate}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="date-red">{ad.endDate}</span>
                    </div>
                    <div>{ad.executionDays}일</div>
                  </td>
                  <td className="center-cell">
                    <div>{ad.totalInflow?.toLocaleString()}건</div>
                    <div>{ad.dailyInflow?.toLocaleString()}건</div>
                    <div>{Number(ad.totalCost)?.toLocaleString()}원</div>
                  </td>
                  <td className="center-cell">
                    <div>
                      {ad.distributorName}/{ad.offerName}
                    </div>
                    <div
                      onClick={(e) => handlePlaceNameClick(ad, e)}
                      style={{
                        cursor: "pointer",
                        color: "#007bff",
                        textDecoration: "underline",
                      }}
                    >
                      {ad.placeName}
                    </div>
                    <div>{ad.placeId}</div>
                  </td>
                  <td className="center-cell">
                    {ad.keywords.map((k, i) => (
                      <div key={k.keywordId || i}>
                        {editingKeyword &&
                        editingKeyword.keywordId === k.keywordId ? (
                          <input
                            type="text"
                            defaultValue={k.keyword}
                            autoFocus
                            onBlur={(e) =>
                              handleKeywordBlur(ad, e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") e.target.blur();
                            }}
                          />
                        ) : (
                          <span>{k.keyword}</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="center-cell">
                    {ad.keywords.map((k, i) => (
                      <div key={k.keywordId || i}>
                        {k.rank || "-"}위 / {k.type || "-"}
                      </div>
                    ))}
                  </td>
                  <td className="center-cell">
                    {!ad.campaignMissions?.some((mission) => mission.save) && (
                      <button
                        className="manage-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSightManageClick(ad);
                        }}
                      >
                        정답 관리
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 정답 관리 모달 */}
        {showSightModal && (
          <div className="modal-overlay">
            <div
              className="modal-content"
              style={{ maxWidth: "800px", maxHeight: "80vh", overflow: "auto" }}
            >
              <h3>정답 관리 - {sightModalData.placeName}</h3>

              <div style={{ marginBottom: "20px" }}>
                <strong>플레이스 ID:</strong> {sightModalData.placeId}
              </div>

              {/* SmartCall 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.smartCall)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      스마트콜 미션 (캠페인 ID: {mission.campaignId})
                    </h4>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>정답:</span>
                      <input
                        type="text"
                        defaultValue={mission.smartCall.answer}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "answer",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  </div>
                ))}

              {/* FindingRoad 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.findingRoad)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      도보 길찾기 미션 (캠페인 ID: {mission.campaignId})
                    </h4>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>순위:</span>
                      <input
                        type="number"
                        defaultValue={mission.findingRoad.sightRank}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "sightRank",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>명소:</span>
                      <input
                        type="text"
                        defaultValue={mission.findingRoad.sightName}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "sightName",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>정답:</span>
                      <input
                        type="text"
                        defaultValue={mission.findingRoad.answer}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "answer",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  </div>
                ))}

              {/* FindingSight 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.findingSight)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      명소 찾기 미션 (캠페인 ID: {mission.campaignId})
                    </h4>

                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>정답:</span>
                      <input
                        type="text"
                        defaultValue={mission.findingSight.answer}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "answer",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  </div>
                ))}

              {/* FindingParkingLot 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.findingParkingLot)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      주차장 찾기 미션 (캠페인 ID: {mission.campaignId})
                    </h4>

                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>정답:</span>
                      <input
                        type="text"
                        defaultValue={mission.findingParkingLot.answer}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "answer",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  </div>
                ))}

              {/* FindingReviewCount 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.findingReviewCount)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      리뷰수 찾기 미션 (캠페인 ID: {mission.campaignId})
                    </h4>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>정답:</span>
                      <input
                        type="text"
                        defaultValue={mission.findingReviewCount.answer}
                        onChange={(e) =>
                          handleSightChange(
                            mission.campaignId,
                            "answer",
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  </div>
                ))}

              {/* Save 미션 */}
              {sightModalData.campaignMissions
                .filter((mission) => mission.save)
                .map((mission, index) => (
                  <div
                    key={mission.campaignId}
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      저장 미션 (캠페인 ID: {mission.campaignId})
                    </h4>
                    <div
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>타입:</span>
                      <span
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {mission.save.type}
                      </span>
                    </div>
                  </div>
                ))}

              <div
                className="modal-buttons"
                style={{ marginTop: "20px", textAlign: "right" }}
              >
                <button
                  onClick={handleSightUpdate}
                  disabled={isLoading || sightChanges.length === 0}
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {isLoading ? "저장 중..." : "저장"}
                </button>
                <button
                  onClick={handleSightCancel}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 플레이스명 변경 모달 */}
        {showPlaceNameModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: "500px" }}>
              <h3>플레이스명 변경</h3>

              <div style={{ marginBottom: "20px" }}>
                <strong>플레이스 ID:</strong> {placeNameModalData.placeId}
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  현재 플레이스명:
                </label>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                    marginBottom: "10px",
                  }}
                >
                  {placeNameModalData.placeName}
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                  }}
                >
                  변경할 플레이스명:
                </label>
                <input
                  type="text"
                  value={newPlaceName}
                  onChange={(e) => setNewPlaceName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                  placeholder="플레이스명을 입력하세요"
                  autoFocus
                />
              </div>

              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "0.9em",
                  color: "#666",
                }}
              >
                <strong>캠페인 ID:</strong>{" "}
                {placeNameModalData.campaignMissions
                  .map((m) => m.campaignId)
                  .join(", ")}
              </div>

              <div
                className="modal-buttons"
                style={{ marginTop: "20px", textAlign: "right" }}
              >
                <button
                  onClick={handlePlaceNameSave}
                  disabled={
                    isLoading ||
                    !newPlaceName ||
                    newPlaceName.trim() === "" ||
                    newPlaceName === placeNameModalData.placeName
                  }
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {isLoading ? "변경 중..." : "변경하기"}
                </button>
                <button
                  onClick={handlePlaceNameCancel}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 키워드 수정 모달 */}
        {showKeywordModal && (
          <div className="modal-overlay">
            <div
              className="modal-content"
              style={{ maxWidth: "800px", maxHeight: "80vh", overflow: "auto" }}
            >
              <h3>키워드 수정 - {keywordModalData.placeName}</h3>

              <div style={{ marginBottom: "20px" }}>
                <strong>플레이스 ID:</strong> {keywordModalData.placeId}
              </div>

              {/* SmartCall 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.smartCall,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    스마트콜 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* FindingRoad 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.findingRoad,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    도보 길찾기 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* FindingSight 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.findingSight,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    명소 찾기 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* FindingParkingLot 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.findingParkingLot,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    주차장 찾기 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* FindingReviewCount 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.findingReviewCount,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    리뷰수 찾기 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Save 미션 */}
              {keywordModalData.campaignMissions.some(
                (mission) => mission.save,
              ) && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                    저장 미션
                  </h4>
                  {keywordModalData.keywords.map((keyword, index) => (
                    <div
                      key={keyword.campaignKeywordId}
                      style={{
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ minWidth: "100px" }}>
                        키워드 {index + 1}:
                      </span>
                      <input
                        type="text"
                        defaultValue={keyword.keyword}
                        readOnly
                        onClick={() => handleKeywordClick(keyword.keyword)}
                        style={{
                          flex: 1,
                          padding: "5px",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ minWidth: "60px" }}>→</span>
                      <input
                        type="text"
                        placeholder="변경할 키워드"
                        onChange={(e) =>
                          handleKeywordChange(
                            keyword.campaignKeywordId,
                            e.target.value,
                          )
                        }
                        style={{ flex: 1, padding: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div
                className="modal-buttons"
                style={{ marginTop: "20px", textAlign: "right" }}
              >
                <button
                  onClick={handleKeywordSave}
                  disabled={isLoading || keywordChanges.length === 0}
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {isLoading ? "저장 중..." : "저장"}
                </button>
                <button
                  onClick={handleKeywordCancel}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pagination-info">
          <div>
            총 {summary.totalCount}개 항목 중 {startIndex} - {endIndex}
          </div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={filter.page === 1}>
              이전
            </button>
            {/* 페이지 번호 버튼들 */}
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={filter.page === page ? "active" : ""}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </button>
              ),
            )}
            <button
              onClick={handleNextPage}
              disabled={endIndex >= summary.totalCount}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AdminCampaignManagement;
